import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * The box every table sits in (TRKC-87): a table wider than its screen scrolls here,
 * never the whole page.
 *
 * While — and only while — the table overflows, the box is a named region in the tab
 * order, so a keyboard can scroll the columns that hold nothing focusable into view
 * (Copilot on #25: the FMCSA candidates are all text, and a book's last columns are
 * badges). A table that fits adds no tab stop and no landmark, which is what desktop
 * sees. The label names the table, the way its caption does.
 */
export function TableScroll({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const element = box.current
    if (element === null) {
      return
    }

    const measure = () => setOverflows(element.scrollWidth > element.clientWidth)
    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    // The box changes size with the screen; the table inside it, with its rows.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    Array.from(element.children).forEach((child) => observer.observe(child))

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={box}
      className={className === undefined ? 'table-scroll' : `table-scroll ${className}`}
      {...(overflows ? { role: 'region', 'aria-label': label, tabIndex: 0 } : {})}
    >
      {children}
    </div>
  )
}

/**
 * A panel spanning a whole row — the paste's row editor, the capture's refusals — in a table
 * wider than its screen. Spread over the row, the cell is as wide as the table, and on a phone
 * the panel's right half was only reached by scrolling the table sideways. Inside it, the
 * panel is as wide as the box's visible part, and held at its left edge while the table
 * scrolls (`.table__panel`). The box holding such rows says so: `table-scroll--panels`.
 */
export function TablePanelRow({
  columns,
  className,
  children,
}: {
  columns: number
  className?: string
  children: ReactNode
}) {
  return (
    <tr className={className}>
      <td colSpan={columns}>
        <div className="table__panel">{children}</div>
      </td>
    </tr>
  )
}
